import { createFileRoute } from '@tanstack/react-router'
import type { JSX } from 'react'

import { PricingStudioScreen } from './-components/pricing-studio-screen.tsx'
import type { PricingInputs } from './-lib/pricing-studio.ts'

/**
 * `?dealId=` carries the HubSpot deal, so a link on the deal record opens the studio
 * already pointed at it. Saving needs the id and nothing else asks HubSpot for anything,
 * which is what keeps this page free of a HubSpot credential.
 *
 * Every other search value is a scenario to start from. The Zap that saves a scenario
 * writes the whole negotiated scenario into the deal's link, so returning to a deal opens
 * on the figures the AE last saved rather than on the book's defaults. That link is a
 * snapshot taken when Save was pressed, NOT a live view of the deal: a fee edited on the
 * deal record afterwards is not in it. Reading the deal instead would mean a HubSpot
 * credential in the backend, which the Zapier hop exists to avoid.
 *
 * Which keys are real inputs is the backend's to say, and it says so in the model's
 * `defaults`. This only decides what is a usable VALUE.
 *
 * @param search - The raw search params.
 * @returns The deal id and the scenario, when the link carried them.
 */
export function validateSearch(search: Record<string, unknown>): {
  dealId?: string
  seed?: PricingInputs
} {
  const dealId = search['dealId']

  // A HubSpot deal id is all digits, and the router JSON-parses search values, so
  // `?dealId=27736888` arrives as a number rather than a string. Rejecting that would
  // drop every real link; coerce it instead, and keep it a string from here on because
  // an id is a label, not a quantity.
  const id =
    typeof dealId === 'number' && Number.isFinite(dealId)
      ? String(dealId)
      : typeof dealId === 'string' && dealId.trim() !== ''
        ? dealId.trim()
        : undefined

  const seed: PricingInputs = {}
  for (const [key, value] of Object.entries(search)) {
    if (key === 'dealId') continue

    // The same values an input may hold: a count or an amount, never negative, as the
    // backend's own check requires; or a toggle. `dealShape` is the one input that holds
    // a word, so it is the one key a string is read for -- otherwise `?rate6051=abc`
    // reaches the quote and the backend refuses the whole scenario over one bad
    // parameter. A new worded input belongs on this line.
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      seed[key] = value
    } else if (typeof value === 'boolean') {
      seed[key] = value
    } else if (
      key === 'dealShape' &&
      typeof value === 'string' &&
      value.trim() !== ''
    ) {
      seed[key] = value.trim()
    }
  }

  return {
    ...(id === undefined ? {} : { dealId: id }),
    ...(Object.keys(seed).length === 0 ? {} : { seed })
  }
}

function PricingStudioPage(): JSX.Element {
  const { dealId, seed } = Route.useSearch()

  return <PricingStudioScreen dealId={dealId} seed={seed} />
}

export const Route = createFileRoute('/sales-tools/pricing-studio')({
  validateSearch,
  component: PricingStudioPage
})
